// An item's price points (ITM-02, ITM-03): rows of label, price in euros and unit, "Aggiungi prezzo", and "Prezzo non
// mostrato" for market-price items, which greys the rows out. A label or a price saves when it loses focus, a unit, an
// added row or a removed one at once. A price that is not an amount is not saved, and says so. The server keeps no id
// on a price point, so rows are keyed by their place in the list.
import { useEffect, useId, useRef, useState } from "react";
import { CloseIcon, PlusIcon } from "@/components/icons";
import { cn } from "@/utils/cn";
import { parsePrice, priceInput } from "../format";
import type { PricePoint, PriceUnit } from "../types";
import { CheckRow, FieldError, fieldClass } from "./drawerFields";

const SUGGESTED = ["Calice", "Bottiglia", "Mezzo litro", "Porzione"];
const UNITS: PriceUnit[] = ["€", "€/kg", "€/hg"];
/** The server keeps at most this many. */
export const PRICES_MAX = 10;

interface Row {
  label: string;
  /** The price as typed, in euros. */
  text: string;
  unit: PriceUnit;
}

interface Props {
  prices: PricePoint[];
  priceHidden: boolean;
  /** A wine: a new row is labelled with whichever of Calice and Bottiglia it lacks. */
  isWine: boolean;
  disabled: boolean;
  /** Why the last save of the prices failed, from the server. */
  error?: string | null;
  onSave: (prices: PricePoint[]) => void;
  onPriceHidden: (hidden: boolean) => void;
}

const toRows = (prices: PricePoint[]): Row[] => prices.map((p) => ({ label: p.label, text: priceInput(p.amountMinor), unit: p.unit }));

/** The rows as price points, or null when a price is not an amount. */
export function rowsToPrices(rows: Row[]): PricePoint[] | null {
  const prices: PricePoint[] = [];
  for (const row of rows) {
    const parsed = parsePrice(row.text);
    if (!parsed.ok) return null;
    prices.push({ label: row.label.trim(), amountMinor: parsed.amountMinor, unit: row.unit });
  }
  return prices;
}

// Field by field: the server's points come with their keys in another order than rowsToPrices writes them, so their
// JSON never matched and every blur or close sent the unchanged prices again.
const same = (a: PricePoint[], b: PricePoint[]) =>
  a.length === b.length && a.every((p, n) => p.label === b[n].label && p.amountMinor === b[n].amountMinor && p.unit === b[n].unit);

export function PricePointsEditor({ prices, priceHidden, isWine, disabled, error, onSave, onPriceHidden }: Props) {
  const listId = useId();
  const [rows, setRows] = useState<Row[]>(() => toRows(prices));
  const [invalid, setInvalid] = useState(false);
  const saved = useRef(prices);
  const box = useRef<HTMLDivElement>(null);
  // While a row is being typed in, the server's copy must not overwrite it.
  const editing = useRef(false);
  const latest = useRef({ rows, onSave });
  latest.current = { rows, onSave };

  const pricesKey = JSON.stringify(prices);
  useEffect(() => {
    saved.current = prices;
    if (!editing.current) setRows(toRows(prices));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the content, not the array's identity
  }, [pricesKey]);

  // A draft the blur never saw (Escape closes the drawer, ‹ › moves on) is saved as the editor goes.
  useEffect(() => () => {
    const next = rowsToPrices(latest.current.rows);
    if (next && !same(next, saved.current)) latest.current.onSave(next);
  }, []);

  const save = (next: Row[]) => {
    setRows(next);
    const parsed = rowsToPrices(next);
    setInvalid(parsed === null);
    if (!parsed || same(parsed, saved.current)) return;
    saved.current = parsed;
    onSave(parsed);
  };
  const change = (index: number, patch: Partial<Row>) => rows.map((r, n) => (n === index ? { ...r, ...patch } : r));

  const add = () => {
    const have = new Set(rows.map((r) => r.label.trim().toLowerCase()));
    const label = isWine ? (["Calice", "Bottiglia"].find((l) => !have.has(l.toLowerCase())) ?? "") : "";
    save([...rows, { label, text: "", unit: "€" }]);
  };

  const off = disabled || priceHidden;

  return (
    <div className="space-y-2">
      <datalist id={listId}>
        {SUGGESTED.map((s) => <option key={s} value={s} />)}
      </datalist>
      <div
        ref={box}
        className={cn("space-y-2", priceHidden && "opacity-50")}
        onFocus={() => {
          editing.current = true;
        }}
        onBlur={(e) => {
          if (box.current?.contains(e.relatedTarget as Node | null)) return;
          editing.current = false;
        }}
      >
        {rows.map((row, n) => (
          // eslint-disable-next-line react/no-array-index-key -- a price point has no id; its place is its identity
          <div key={n} className="flex items-center gap-2" role="group" aria-label={`Prezzo ${n + 1}`}>
            <input
              type="text"
              value={row.label}
              list={listId}
              placeholder="Es. Calice"
              aria-label={`Etichetta del prezzo ${n + 1}`}
              disabled={off}
              enterKeyHint="done"
              onChange={(e) => setRows(change(n, { label: e.target.value }))}
              onBlur={() => save(rows)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              className={cn(fieldClass, "min-w-0 flex-1")}
            />
            <div className="relative w-[96px] shrink-0">
              <input
                type="text"
                inputMode="decimal"
                value={row.text}
                placeholder="0,00"
                aria-label={`Importo del prezzo ${n + 1}, in euro`}
                aria-invalid={!parsePrice(row.text).ok || undefined}
                disabled={off}
                enterKeyHint="done"
                onChange={(e) => setRows(change(n, { text: e.target.value }))}
                onBlur={() => {
                  const parsed = parsePrice(row.text);
                  // A valid amount reads back as the input shows it: "7.5" becomes "7,50".
                  save(parsed.ok ? change(n, { text: priceInput(parsed.amountMinor) }) : rows);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                }}
                className={cn(fieldClass, "pr-7 text-right tabular-nums", !parsePrice(row.text).ok && "border-red-400")}
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400">€</span>
            </div>
            <select
              value={row.unit}
              disabled={off}
              aria-label={`Unità del prezzo ${n + 1}`}
              onChange={(e) => save(change(n, { unit: UNITS.find((u) => u === e.target.value) ?? "€" }))}
              className={cn(fieldClass, "w-[72px] shrink-0 pl-2 pr-1")}
            >
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <button
              type="button"
              onClick={() => save(rows.filter((_, k) => k !== n))}
              disabled={off}
              aria-label={`Togli il prezzo ${n + 1}`}
              className="-mr-1 grid h-[32px] w-[28px] shrink-0 place-items-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            >
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      <FieldError message={invalid ? "Prezzo non salvato: scrivi un importo, come 7,50." : error} />
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        {rows.length < PRICES_MAX ? (
          <button
            type="button"
            onClick={add}
            disabled={off}
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-1 text-[13px] font-medium text-indigo-700 hover:text-indigo-900 disabled:opacity-50"
          >
            <PlusIcon className="h-4 w-4" />
            Aggiungi prezzo
          </button>
        ) : <span />}
        <CheckRow checked={priceHidden} disabled={disabled} onChange={onPriceHidden} label="Prezzo non mostrato" />
      </div>
    </div>
  );
}
